import { CommonModule, formatDate } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { COUNTRIES } from '@core/data/countries-states';
//
import { NIGERIAN_STATES } from '@core/data/nigerian-states';
import { NotificationFacade } from '@core/facades/notification.facade';
import { UserKyc } from '@core/models/admin/account';
import {
  CommonService,
  UserDetailsValidator,
} from '@core/services/common.service';
import { IndividualRegistrationService } from '@core/services/individual-registration.service';
import { LoadingService } from '@core/services/loading.service';
import { ReportService } from '@core/services/report.service';
import { CustomValidators } from '@core/validators/custom-validator';
import { BaseSelectDirective } from '@shared/directives/base-select.directive';
import {
  ButtonDirective,
  SpinDirective,
} from '@shared/directives/button.directive';
import { InputDateDirective } from '@shared/directives/input-date.directive';
import { InputLabelComponent } from '@shared/ui/input-label/input-label.component';
import { InputComponent } from '@shared/ui/input/input.component';
import { KycAccordionComponent } from '@shared/ui/kyc-accordion/kyc-accordion.component';
import {
  ANNUAL_INCOME,
  FUND_SOURCE,
  GENDER,
  MARITAL_STATUS,
  NIGERIAN_TITLES,
} from 'app/app.constants';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  combineLatest,
  debounce,
  debounceTime,
  distinctUntilChanged,
  first,
} from 'rxjs';

@Component({
  selector: 'app-edit-customer-information',
  standalone: true,
  imports: [
    KycAccordionComponent,
    CommonModule,
    ReactiveFormsModule,
    InputLabelComponent,
    InputComponent,
    InputDateDirective,
    BaseSelectDirective,
    ButtonDirective,
    SpinDirective,
  ],
  templateUrl: './edit-customer-information.component.html',
  styleUrl: './edit-customer-information.component.scss',
})
export class EditCustomerInformationComponent implements OnInit, OnDestroy {
  @Input() customerInformation!: UserKyc;

  isLoading: boolean = false;
  customerInfoForm!: FormGroup;
  formattedDob: string = '';
  nigerianStates = NIGERIAN_STATES;
  countries = COUNTRIES.data;
  titles = Object.entries(NIGERIAN_TITLES).map(([key, value]) => ({
    key,
    value,
  }));
  annualIncomeRange: any[] = [];
  maritalStatus = MARITAL_STATUS;
  genderList = GENDER;
  sourceoffunds: any[] = [];
  subs: Subscription[] = [];
  isLoading$!: Observable<boolean>;
  isValidating$ = new BehaviorSubject<boolean>(false);
  accountId: string = '';

  fb = inject(FormBuilder);
  route = inject(ActivatedRoute);
  individualService = inject(IndividualRegistrationService);
  cdr = inject(ChangeDetectorRef);
  loadingService = inject(LoadingService);
  toast = inject(NotificationFacade);
  reportService = inject(ReportService);
  commonService = inject(CommonService);
  notify = inject(NotificationFacade);

  ngOnInit(): void {
    this.getAllEnums();
    this.isLoading$ =
      this.loadingService.getLoadingObservable('update-profile');
    this.createForm();

    if (this.customerInfoForm) {
      this.setFormValues();
      this.watchFormValueChanges();
    }

    this.accountId = this.route.snapshot.parent?.params['accountId'];
  }
   getAllEnums() {
    const sub = this.reportService
      .getAllEnums()
      .subscribe({
        next: (response) => {
          this.isLoading = false;
          this.annualIncomeRange = this.convertApiToDropdown(response?.data?.AnnualIncome);
          this.sourceoffunds = this.convertApiToDropdown(response?.data?.SourceOfFund);
          this.cdr.detectChanges();
        },
        error: (resp) => {
          this.cdr.detectChanges();
        },
      });
    this.subs.push(sub);
  }
  convertApiToDropdown(apiData: string[]): { value: string; label: string }[] {
    return apiData.map((item) => {    
      return { value: item, label: item };
    });
  }

  createForm() {
    this.customerInfoForm = this.fb.nonNullable.group({
      title: [''],
      first_name: ['', Validators.minLength(2)],
      last_name: ['', Validators.minLength(2)],
      middle_name: ['', Validators.minLength(2)],
      gender: [''],
      email: ['', [Validators.email, Validators.required]],
      phone_number_1: [''],
      phone_number_2: [''],
      dob: [new Date()],
      marital_status: [''],
      nationality: [''],
      annual_income: ['', Validators.required],
      source_of_fund: [''],
      state_of_origin: [''],
      place_of_birth: [''],
      political_exposure: [''],
      bvn: ['', [Validators.required, CustomValidators.bvn()]],
      // DO NOT REMOVE
      profile_id: [''],
    });
  }

  setFormValues() {
    if (this.customerInformation) {
      this.customerInfoForm.patchValue({
        title: this.customerInformation.title || '',
        first_name: this.customerInformation.first_name,
        last_name: this.customerInformation.last_name,
        middle_name: this.customerInformation.middle_name || '',
        gender: this.customerInformation.gender || '',
        email: this.customerInformation.email,
        phone_number_1: this.customerInformation.phone_number_1 || '',
        phone_number_2: this.customerInformation.phone_number_2 || '',
        marital_status: this.customerInformation.marital_status || '',
        nationality: this.customerInformation.nationality || '',
        annual_income: this.customerInformation.annual_income || '',
        source_of_fund: this.customerInformation.source_of_fund || '',
        state_of_origin: this.customerInformation.state_of_origin || '',
        bvn: this.customerInformation.bvn || '',
        place_of_birth: this.customerInformation.place_of_birth || '',
        political_exposure: this.customerInformation.political_exposure || '',
        profile_id: this.customerInformation.id,
      });

      this.customerInfoForm.controls['dob'].setValue(
        formatDate(this.customerInformation.dob, 'yyyy-MM-dd', 'en')
      );
    }
  }

  updateCustomerProfile() {
    const { email, phone_number_1, phone_number_2, bvn } =
      this.validationStatus;

    if (!email || !phone_number_1 || !phone_number_2 || !bvn) {
      this.toast.error('Please correct the invalid fields before submitting');
      return;
    }
    const payload = {
      ...this.customerInfoForm.getRawValue(),
      account_id: this.accountId
    }
    const sub = this.individualService
      .updateCustomerProfile(payload)
      .subscribe({
        next: () => {
          this.toast.success('Successfully updated profile');
        },
        error: () => {
          this.toast.error('Failed to update profile');
        },
      });

    this.subs.push(sub);
  }

  private watchFieldChange(field: string) {
    const control = this.customerInfoForm.get(field);
    if (!control) return;

    const initialValue = this.customerInformation?.[field as keyof UserKyc];

    const controlSub = control.valueChanges
      .pipe(debounceTime(1000), distinctUntilChanged())
      .subscribe((value) => {
        // Skip validation if value hasn't changed from original
        if (value === initialValue) {
          this.validationStatus[field as keyof typeof this.validationStatus] =
            true;
          return;
        }

        // Validation checks
        if (
          (field === 'phone_number_1' || field === 'phone_number_2') &&
          value.length < 8
        ) {
          this.isValidating$.next(false);
          this.validationStatus[field as keyof typeof this.validationStatus] =
            false;
          return;
        }

        if (field === 'bvn' && value.length < 11) {
          this.isValidating$.next(false);
          this.validationStatus[field as keyof typeof this.validationStatus] =
            false;
          return;
        }

        if (value) {
          this.isValidating$.next(true);

          const key =
            field === 'phone_number_1' || field === 'phone_number_2'
              ? 'phone_number'
              : field;

          const payload = { [key]: value };

          const sub = this.commonService
            .validateUserDetails(payload)
            .subscribe({
              next: () => {
                this.isValidating$.next(false);
                this.validationStatus[
                  field as keyof typeof this.validationStatus
                ] = true;
              },
              error: () => {
                this.isValidating$.next(false);
                this.validationStatus[
                  field as keyof typeof this.validationStatus
                ] = false;
                this.customerInfoForm.get(field)?.setErrors({ invalid: true });
              },
            });

          this.subs.push(sub);
        }
      });

    this.subs.push(controlSub);
  }

  watchFormValueChanges() {
    ['email', 'phone_number_1', 'phone_number_2', 'bvn'].forEach(
      (field: string) => this.watchFieldChange(field)
    );
  }

  private validationStatus = {
    email: true,
    phone_number_1: true,
    phone_number_2: true,
    bvn: true,
  };

  allFieldsValid(): boolean {
    const { email, phone_number_1, phone_number_2, bvn } =
      this.validationStatus;
    return email && phone_number_1 && phone_number_2 && bvn;
  }
  ngOnDestroy(): void {
    this.subs.forEach((sub) => sub.unsubscribe());
  }
}
